import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { ArrowsLeftRight } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function requestNotificationPermission() {
  if ('Notification' in window && Notification.permission === 'default') {
    Notification.requestPermission();
  }
}

function sendBrowserNotification(title, body) {
  if ('Notification' in window && Notification.permission === 'granted') {
    try {
      const n = new Notification(title, {
        body,
        icon: 'https://customer-assets.emergentagent.com/job_medform-standby/artifacts/3xn9wu25_standby_transparent.png',
        tag: 'transfer-notification',
        requireInteraction: true,
      });
      n.onclick = () => { window.focus(); n.close(); };
    } catch (err) {
      console.warn('Browser notification failed:', err.message);
    }
  }
}

export function TransferNotification() {
  const { token } = useAuth();
  const [incomingTransfer, setIncomingTransfer] = useState(null);
  const [showAcceptDialog, setShowAcceptDialog] = useState(false);
  const [showPinDialog, setShowPinDialog] = useState(false);
  const [pinInput, setPinInput] = useState('');
  const [accepting, setAccepting] = useState(false);
  const lastNotifiedId = useRef(null);

  // Request permission on mount
  useEffect(() => { requestNotificationPermission(); }, []);

  const checkIncoming = useCallback(async () => {
    if (!token) return;
    try {
      const { data } = await axios.get(`${API_URL}/api/patients/transfers/incoming`);
      if (data.transfers?.length > 0 && !showAcceptDialog && !showPinDialog) {
        const transfer = data.transfers[0];
        setIncomingTransfer(transfer);
        setShowAcceptDialog(true);

        // Send browser push notification (only once per transfer)
        if (transfer.transfer_id !== lastNotifiedId.current) {
          lastNotifiedId.current = transfer.transfer_id;
          sendBrowserNotification(
            'Incoming Patient Transfer',
            `${transfer.from_user_name} is transferring a patient to your care.`
          );
        }
      }
    } catch (err) {
      console.warn('Transfer polling failed (non-critical):', err.message);
    }
  }, [token, showAcceptDialog, showPinDialog]);

  useEffect(() => {
    const interval = setInterval(checkIncoming, 5000);
    checkIncoming();
    return () => clearInterval(interval);
  }, [checkIncoming]);

  const handleAccept = () => {
    setShowAcceptDialog(false);
    setPinInput('');
    setShowPinDialog(true);
  };

  const handleDecline = () => {
    setShowAcceptDialog(false);
    setIncomingTransfer(null);
  };

  const handleConfirmPin = async () => {
    if (!incomingTransfer || pinInput.length !== 6) return;
    setAccepting(true);
    try {
      await axios.post(
        `${API_URL}/api/patients/${incomingTransfer.patient_id}/transfer/accept`,
        { pin: pinInput }
      );
      toast.success('Patient transfer accepted! The patient is now in your care.');
      setShowPinDialog(false);
      setIncomingTransfer(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid PIN or transfer failed');
    } finally {
      setAccepting(false);
    }
  };

  return (
    <>
      {/* Accept / Decline Dialog */}
      <AlertDialog open={showAcceptDialog} onOpenChange={(open) => { if (!open) handleDecline(); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-purple-700">
              <ArrowsLeftRight size={24} weight="bold" />
              Incoming Patient Transfer
            </AlertDialogTitle>
            <AlertDialogDescription className="text-sm text-slate-600 leading-relaxed">
              <strong>{incomingTransfer?.from_user_name}</strong> is requesting to transfer a patient to your care. Do you accept this transfer?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleDecline} data-testid="decline-transfer-button">
              Decline
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleAccept}
              className="bg-purple-600 hover:bg-purple-700"
              data-testid="accept-transfer-button"
            >
              Accept Transfer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* PIN Entry Dialog */}
      <AlertDialog open={showPinDialog} onOpenChange={(open) => { if (!open) { setShowPinDialog(false); setIncomingTransfer(null); } }}>
        <AlertDialogContent className="max-w-sm">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-purple-700">
              <ArrowsLeftRight size={24} weight="bold" />
              Enter Transfer PIN
            </AlertDialogTitle>
            <AlertDialogDescription className="text-sm text-slate-600">
              Enter the 6-digit PIN provided by <strong>{incomingTransfer?.from_user_name}</strong> to confirm the patient transfer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-4">
            <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">PIN Code</Label>
            <Input
              value={pinInput}
              onChange={(e) => setPinInput(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="000000"
              className="rounded-sm border-slate-300 text-center text-2xl font-mono tracking-[0.3em] mt-2"
              maxLength={6}
              autoFocus
              data-testid="incoming-transfer-pin-input"
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => { setShowPinDialog(false); setIncomingTransfer(null); }}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={pinInput.length !== 6 || accepting}
              onClick={handleConfirmPin}
              className="bg-purple-600 hover:bg-purple-700"
              data-testid="confirm-incoming-transfer-button"
            >
              {accepting ? 'Confirming...' : 'Confirm Transfer'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
